import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MuLink from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Form from 'components/form';
import TextField from 'components/form/TextInput';
import Checkbox from 'components/form/Checkbox';
import Button from 'components/Button';
import cookie from 'utils/cookie';
import * as CONFIG from 'constants/config';
import * as Styled from './styled';
import Typography from '@mui/material/Typography';
import useTimer from 'hooks/useTimer';
import toast, { snack } from 'utils/toast';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import LoginMFA from './LoginMFA';
import { ReactComponent as GoogleIcon } from 'assets/svg/google-icon.svg';
import { ReactComponent as MicrosoftIcon } from 'assets/svg/microsoft-icon.svg';

const FormItem = Form.FormItem;

const LoginForm = ({
  modal,
  form: { getFieldDecorator, validateFields, setFieldsValue },
  onSubmit,
  onResendCode,
  showMfa,
  loading,
}) => {
  const [t] = useTranslation(['auth']);
  const { timer, start, resendDisabled } = useTimer();
  const otpRef = useRef();

  useEffect(() => {
    try {
      const username = cookie.get('username');
      if (username) {
        setFieldsValue({ email: username, remember: true });
      }
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    if (showMfa) {
      start();
    }
  }, [showMfa]);

  const handleSubmit = (e) => {
    e.preventDefault();
    return validateFields().then(onSubmit);
  };

  return (
    <Styled.LoginForm modal={modal} elevation={0}>
      <Box mb={3}>
        {showMfa ? (
          <Typography variant="h1" fontWeight={400} lineHeight={1.2} paragraph>
            We Secure Your Account With
            <br />
            Two-Factor Authentication
            <br />
            <Box mt={1} sx={{ color: 'primary.main', fontWeight: 700 }}>
              Verify Your Account
            </Box>
          </Typography>
        ) : (
          <Typography textAlign="center" variant="h2" fontSize={28}>
            Login to your Account
          </Typography>
        )}
      </Box>
      {showMfa ? (
        <LoginMFA {...showMfa} onSubmit={onSubmit} loading={loading} />
      ) : (
        <Form onSubmit={handleSubmit}>
          <div className="fields">
            <FormItem>
              {getFieldDecorator('email', {
                rules: [
                  {
                    required: true,
                    type: 'email',
                    message: t(
                      'auth:invalid-email-error',
                      'Please enter a valid email address',
                    ),
                  },
                ],
              })(
                <TextField
                  startAdornment={<EmailIcon />}
                  label="Email"
                  placeholder={t('auth:email-label', 'Email Address')}
                  type="email"
                  fullWidth
                />,
              )}
            </FormItem>
            <FormItem>
              {getFieldDecorator('password', {
                rules: [
                  {
                    required: true,
                    message: t('auth:password-error', 'Please enter password'),
                  },
                ],
              })(
                <TextField
                  startAdornment={<LockIcon />}
                  label="Password"
                  placeholder={t('auth:password-label', 'Password')}
                  type="password"
                  fullWidth
                />,
              )}
            </FormItem>
            <Box
              mt={2}
              mb={1}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <FormItem>
                {getFieldDecorator('remember', {
                  valuePropName: 'checked',
                })(
                  <Checkbox
                    label={t('auth:remember-me', 'Remember me')}
                    id="remember"
                  />,
                )}
              </FormItem>
              <MuLink component={Link} to="/forgot-password">
                {t('auth:forgot-password-link', 'Forgot Password?')}
              </MuLink>
            </Box>
            <Box>
              <Button fullWidth loading={loading} size="large" type="submit">
                {t('auth:login-button', 'Login')}
              </Button>
            </Box>
          </div>
          <Divider sx={{ my: 2 }}>Or</Divider>
          <Stack
            direction={modal ? 'column' : 'row'}
            spacing={2}
            sx={{ mb: 1 }}
          >
            <Button
              fullWidth
              color="grayed"
              variant="outlined"
              startIcon={<GoogleIcon />}
              component="a"
              href={`${CONFIG.REACT_APP_API_URL}API/1_0/googleLogin.php`}
            >
              Sign in with Google
            </Button>
            <Button
              fullWidth
              color="grayed"
              variant="outlined"
              startIcon={<MicrosoftIcon />}
              component="a"
              href={`${CONFIG.REACT_APP_API_URL}API/1_0/microsoftLogin.php`}
            >
              Sign in with Microsoft
            </Button>
          </Stack>
          {!modal && (
            <Box mt={2}>
              <Box textAlign="center" fontWeight={500}>
                <Typography fontWeight={500} component="span">
                  Not a member?{' '}
                </Typography>
                <Button
                  size="small"
                  component="a"
                  variant="link"
                  href={`${CONFIG.WEBSITE}/pricing`}
                >
                  {t('auth:create-account-link', 'Create A New Account')}
                </Button>
              </Box>
            </Box>
          )}
        </Form>
      )}
    </Styled.LoginForm>
  );
};

export default Form.create()(LoginForm);
